// Background music manager for Pistol Duel
import { isMuted } from './audio';

const INTRO_TRACK = '/music/intro.ogg';
const GAMEPLAY_TRACKS = [
  '/music/track-1.ogg',
  '/music/track-2.ogg',
  '/music/track-3.ogg',
  '/music/track-4.ogg',
];

const MUSIC_VOLUME = 0.3;
const CROSSFADE_DURATION = 800; // ms

let currentAudio: HTMLAudioElement | null = null;
let mode: 'intro' | 'gameplay' | 'none' = 'none';
let lastTrackIndex = -1;

function pickRandomTrack(): string {
  let idx: number;
  do {
    idx = Math.floor(Math.random() * GAMEPLAY_TRACKS.length);
  } while (idx === lastTrackIndex && GAMEPLAY_TRACKS.length > 1);
  lastTrackIndex = idx;
  return GAMEPLAY_TRACKS[idx];
}

function fadeOut(audio: HTMLAudioElement, duration: number): Promise<void> {
  return new Promise((resolve) => {
    const startVol = audio.volume;
    const steps = 20;
    const stepTime = duration / steps;
    let step = 0;
    const interval = setInterval(() => {
      step++;
      audio.volume = Math.max(0, startVol * (1 - step / steps));
      if (step >= steps) {
        clearInterval(interval);
        audio.pause();
        audio.removeAttribute('src');
        audio.load();
        resolve();
      }
    }, stepTime);
  });
}

function fadeIn(audio: HTMLAudioElement, targetVolume: number, duration: number) {
  audio.volume = 0;
  audio.play().catch(() => {});
  const steps = 20;
  const stepTime = duration / steps;
  let step = 0;
  const interval = setInterval(() => {
    step++;
    audio.volume = Math.min(targetVolume, targetVolume * (step / steps));
    if (step >= steps) {
      clearInterval(interval);
    }
  }, stepTime);
}

function stopCurrent() {
  if (currentAudio) {
    currentAudio.pause();
    currentAudio.removeAttribute('src');
    currentAudio.load();
    currentAudio = null;
  }
  mode = 'none';
}

export function startIntroMusic() {
  if (mode === 'intro' && currentAudio) return;
  stopCurrent();
  if (isMuted()) { mode = 'intro'; return; }

  const audio = new Audio(INTRO_TRACK);
  audio.loop = true;
  audio.volume = 0;
  currentAudio = audio;
  mode = 'intro';
  fadeIn(audio, MUSIC_VOLUME, CROSSFADE_DURATION);
}

export function startGameplayMusic() {
  if (mode === 'gameplay' && currentAudio) return;
  
  if (currentAudio && mode === 'intro') {
    const oldAudio = currentAudio;
    currentAudio = null;
    mode = 'gameplay';
    fadeOut(oldAudio, CROSSFADE_DURATION).then(() => {
      if (mode === 'gameplay') playNextGameplayTrack();
    });
  } else {
    stopCurrent();
    mode = 'gameplay';
    if (isMuted()) return;
    playNextGameplayTrack();
  }
}

function playNextGameplayTrack() {
  if (mode !== 'gameplay') return;
  if (isMuted()) return;
  const src = pickRandomTrack();
  const audio = new Audio(src);
  audio.volume = 0;
  audio.addEventListener('ended', () => {
    if (mode === 'gameplay') {
      playNextGameplayTrack();
    }
  });
  currentAudio = audio;
  fadeIn(audio, MUSIC_VOLUME, CROSSFADE_DURATION);
}

export function stopMusic() {
  if (currentAudio) {
    const oldAudio = currentAudio;
    currentAudio = null;
    mode = 'none';
    fadeOut(oldAudio, CROSSFADE_DURATION);
  } else {
    mode = 'none';
  }
}

export function setMusicMuted(muted: boolean) {
  if (!currentAudio) return;
  if (muted) {
    currentAudio.pause();
  } else {
    currentAudio.play().catch(() => {});
  }
}
